import React from 'react';
import { X, Heart, ExternalLink, CheckCircle } from 'lucide-react';

const DonationModal = ({ 
  isOpen, 
  onClose, 
  donationUrl, 
  donationQrImage, 
  suggestedAmounts = [],
  isAvailable = false,
  opportunityTitle = ""
}) => {
  if (!isOpen) return null;

  // Default suggested amounts if none provided
  const defaultAmounts = [
    { label: "Per opportunity posted", amount: "$10–$25" },
    { label: "Monthly recurring", amount: "$10/month" },
    { label: "Annual underwriting", amount: "$250/year" }
  ];
  
  const amounts = suggestedAmounts.length > 0 ? suggestedAmounts : defaultAmounts;

  const handleDonateClick = () => {
    if (donationUrl) {
      window.open(donationUrl, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" data-testid="donation-modal">
      <div className="bg-white rounded-xl max-w-md w-full shadow-xl">
        {/* Success Header */}
        <div className="bg-gradient-to-r from-[#8B2E3C] to-[#A63D4C] rounded-t-xl p-6 text-white">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                <CheckCircle className="w-7 h-7" />
              </div>
              <div>
                <h2 className="text-xl font-bold">Opportunity Posted!</h2>
                <p className="text-sm opacity-90">Thank you for creating a volunteer opportunity</p>
              </div>
            </div>
            <button 
              onClick={onClose} 
              className="text-white opacity-80 hover:opacity-100 transition-opacity"
              data-testid="close-donation-modal"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
          {opportunityTitle && (
            <p className="text-sm opacity-90 bg-white bg-opacity-10 rounded-lg px-3 py-2 truncate">
              "{opportunityTitle}"
            </p>
          )}
        </div>

        {/* Donation Content */}
        <div className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-[#FBF4EA] rounded-full flex items-center justify-center">
              <Heart className="w-5 h-5 text-[#8B2E3C]" />
            </div>
            <div>
              <h3 className="font-bold text-[#222222]">Support this tool</h3>
              <p className="text-sm text-[#666666]">Help keep Community Kindness Circle running</p>
            </div>
          </div>

          <div className="space-y-2 mb-4">
            <p className="text-sm text-[#666666]">Suggested donation levels:</p>
            {amounts.map((item, index) => (
              <div key={`${item.label}-${item.amount}`} className="flex items-center justify-between text-sm bg-[#FBF4EA] rounded-lg px-3 py-2">
                <span className="text-[#666666]">{item.label}</span>
                <span className="font-medium text-[#8B2E3C]">{item.amount}</span>
              </div>
            ))}
          </div>

          <p className="text-sm text-[#666666] mb-4">
            Donations sustain hosting, upkeep, and support. We'll share periodic updates on usage and community impact.
          </p>

          {donationQrImage && (
            <div className="flex justify-center mb-4">
              <img 
                src={donationQrImage} 
                alt="Donation QR Code" 
                className="w-28 h-28 border-2 border-[#E9DDCF] rounded-lg"
              />
            </div>
          )}

          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="btn-outline flex-1"
              data-testid="maybe-later-button"
            >
              Maybe later
            </button>
            {isAvailable ? (
              <button
                onClick={handleDonateClick}
                className="btn-primary flex-1 flex items-center justify-center gap-2"
                data-testid="donate-now-button"
              >
                <Heart className="w-4 h-4" />
                Support the tool
                <ExternalLink className="w-4 h-4" />
              </button>
            ) : (
              <button
                disabled
                className="btn-primary flex-1 opacity-50 cursor-not-allowed"
              >
                Coming soon
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DonationModal;
